import React, { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { uploadImage, assetUrl } from "../lib/api";

/**
 * ImageUpload — file upload widget that returns the uploaded URL via onChange.
 * Always shows a preview if value is set. Optional fallback URL input is hidden
 * unless `allowUrl` is true.
 */
export function ImageUpload({ value, onChange, label, testId, size = 96, allowUrl = false, recommended = "" }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setErr("File too large (max 5MB)");
            return;
        }
        setErr("");
        setBusy(true);
        try {
            const res = await uploadImage(file);
            onChange?.(res.url);
        } catch (ex) {
            setErr(ex?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const clear = () => onChange?.("");

    return (
        <div className="space-y-2" data-testid={testId || "image-upload"}>
            {label && (
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#64748B]">{label}</p>
            )}
            <div className="flex items-center gap-3">
                <div
                    className="rounded-md border border-dashed border-[#1E2233] bg-[#0A0B10] flex items-center justify-center overflow-hidden shrink-0"
                    style={{ width: size, height: size }}
                >
                    {value ? (
                        <img src={assetUrl(value)} alt="upload preview" className="w-full h-full object-cover" data-testid={`${testId || "image-upload"}-preview`} />
                    ) : (
                        <ImageIcon className="h-6 w-6 text-[#3A3F55]" />
                    )}
                </div>
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                        onChange={handleFile}
                        className="hidden"
                        data-testid={`${testId || "image-upload"}-input`}
                    />
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            className="btn-secondary text-xs inline-flex items-center gap-1.5"
                            data-testid={`${testId || "image-upload"}-btn`}
                        >
                            <Upload className="h-3.5 w-3.5" />
                            {busy ? "Uploading…" : value ? "Replace" : "Upload image"}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={clear}
                                className="btn-secondary text-xs inline-flex items-center gap-1.5"
                                data-testid={`${testId || "image-upload"}-clear`}
                            >
                                <X className="h-3.5 w-3.5" /> Remove
                            </button>
                        )}
                    </div>
                    {allowUrl && (
                        <input
                            type="text"
                            value={value || ""}
                            onChange={(e) => onChange?.(e.target.value)}
                            placeholder="…or paste an image URL"
                            className="bg-[#0A0B10] border border-[#1E2233] px-2 py-1 rounded text-xs font-mono w-full"
                            data-testid={`${testId || "image-upload"}-url`}
                        />
                    )}
                    {err && <p className="text-xs text-[#EF4444]">{err}</p>}
                    <p className="text-[10px] text-[#64748B]">
                        {recommended ? <span className="text-[#94A3B8]">Recommended: {recommended}. </span> : null}
                        PNG, JPG, WebP, GIF or SVG. Max 5MB.
                    </p>
                </div>
            </div>
        </div>
    );
}
